import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContactTypes,
  IAccountRespSchema,
  IContactRespSchema,
  IQuoteContactAddressDataInputSchema,
  IQuoteContactAddressDataSchema,
  IQuoteContacts,
  IQuoteContactsRequest,
  IQuoteRequestSchema,
  IQuoteRespSchema,
  QuoteContactAddressDataSchema,
} from '@monetize/types/app';
import { useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
  getAccountById,
  useGetAccountById,
} from '../../../../../api/accountsService';
import { handleApiErrorToast } from '../../../../../api/axios';
import { useGetEsignContacts } from '../../../../../api/contactsService';
import {
  updateContactByQuote,
  updateQuoteAddress,
} from '../../../../../api/cpqService';
import { enqueueForQuote } from '../../../../../api/quoteWriteChain';
import {
  QUOTE_ESIGN_CONTACT_EDITABLE_STATUSES,
  QUOTE_PRIMARY_BILLING_CONTACT_EDITABLE_STATUSES,
  QUOTE_READONLY_STATUSES,
} from '../../../../../constants/quotes';
import { useIsTenantEsignConfigured } from '../../../../../hooks/useIsTenantEsignConfigured';
import { getQuoteStatus } from '../../../../../utils';

export interface UseQuoteContactsProps {
  initialQuoteContacts?: IQuoteContacts;
  quoteId?: string;
  quote?: IQuoteRespSchema | null;
  /**
   * False if quote is not in DRAFT or ACCEPTED_ADMIN_EDITABLE or user does not have write access
   */
  isReadOnly?: boolean;
  /**
   * If true, the user is not allowed to edit the quote based on role or quote sharing
   */
  isReadOnlyQuote: boolean;
  accountId?: string;
  evaluateQuoteRules: (quoteId: string) => void;
  quoteFormMethods: UseFormReturn<IQuoteRequestSchema>;
  handleSubmitButtonWithoutDirtyCheck: () => void;
}

const defaultQuoteContacts: IQuoteContacts = {
  primary: null,
  billing: null,
  esign: [],
  additionalESignRecipients: [],
  additionalSigners: [],
};

/**
 * Logic to support Quote Contacts
 */
export function useQuoteContacts({
  initialQuoteContacts,
  quoteId,
  quote,
  isReadOnly,
  isReadOnlyQuote,
  accountId,
  evaluateQuoteRules,
  quoteFormMethods,
  handleSubmitButtonWithoutDirtyCheck,
}: UseQuoteContactsProps) {
  const { data: accountDetails } = useGetAccountById(accountId!, {
    enabled: !!accountId,
    refetchOnWindowFocus: false,
  });

  const [linkedAccount, setLinkedAccount] = useState<IAccountRespSchema | null>(
    null,
  );
  const [billingAccountId, setBillingAccountId] = useState<string | null>(
    quote?.billingAccountId || null,
  );
  const [shippingAccountId, setShippingAccountId] = useState<string | null>(
    quote?.accountId || null,
  );
  const [esignCustomerAccountId, setEsignCustomerAccountId] = useState<
    string | null
  >(
    initialQuoteContacts?.esign.find((contact) => !contact.internal)
      ?.accountId ||
      quote?.accountId ||
      null,
  );
  const accountSelectItems = useMemo(
    () =>
      [
        ...(accountDetails ? [accountDetails] : []),
        ...(linkedAccount ? [linkedAccount] : []),
      ].sort((a, b) => (a.id === billingAccountId ? -1 : 1)),
    [accountDetails, linkedAccount, billingAccountId],
  );

  const handleChangeLinkedAccount = async (
    account: IAccountRespSchema | null,
  ) => {
    setLinkedAccount(account);
    quoteFormMethods.setValue('linkedAccountId', account?.id);
    quoteFormMethods.setValue('billingAccountId', quote?.accountId!);
    setShippingAccountId(quote?.accountId!);
    setBillingAccountId(quote?.accountId!);
    const newQuoteContacts = {
      ...quoteContacts,
      primary: null,
      billing: null,
      esign: [],
      additionalESignRecipients: [],
      additionalSigners: [],
    };
    setQuoteContacts(newQuoteContacts);
    await handleSave({ newQuoteContacts });

    handleSubmitButtonWithoutDirtyCheck();
  };

  // When quote changes, set the secondary account to the quote's account
  useEffect(() => {
    (async () => {
      if (quote?.linkedAccountId && !linkedAccount) {
        try {
          const account = await getAccountById(quote?.linkedAccountId);
          setLinkedAccount(account);
        } catch (error) {
          setLinkedAccount(null);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.linkedAccountId]);

  const handleChangeBillingAccount = async (accId: string | null) => {
    if (accId === billingAccountId || !accId) {
      return;
    }

    setBillingAccountId(accId);
    quoteFormMethods.setValue('billingAccountId', accId);
    setQuoteContacts({
      ...quoteContacts,
      billing: null,
    });
    await handleSave({ newQuoteContacts: { ...quoteContacts, billing: null } });
    handleSubmitButtonWithoutDirtyCheck();
  };

  const handleChangeShippingAccountId = (accId: string | null) => {
    if (accId === shippingAccountId || !accId) {
      return;
    }

    setShippingAccountId(accId);
    setQuoteContacts({
      ...quoteContacts,
      primary: null,
    });
  };

  const handleEsignCustomerAccountIdChange = (accId: string | null) => {
    if (accId === esignCustomerAccountId) {
      return;
    }
    setEsignCustomerAccountId(accId);
    setQuoteContacts({
      ...quoteContacts,
      esign: quoteContacts.esign.filter((contact) => contact.internal),
    });
  };

  const handleResetEsignContacts = () => {
    setEsignCustomerAccountId(quote?.accountId || null);
    setAdditionalRecipientSelectedContacts([]);
    setAdditionalSigners([]);
    setQuoteContacts({
      ...quoteContacts,
      esign: [],
      additionalESignRecipients: [],
      additionalSigners: [],
    });
  };

  const {
    allContacts: internalAccountEsignContacts,
    isLoading: loadingInternalEsignContacts,
  } = useGetEsignContacts({ accountId, refetchOnWindowFocus: false });
  const [isLoading, setIsLoading] = useState(false);

  const isTenantEsignConfigured = useIsTenantEsignConfigured();
  const isEsignEnabled = isTenantEsignConfigured && !!quote?.requiresEsign;
  const [isDisabled, setIsDisabled] = useState(isReadOnlyQuote);
  const [isDisabledPrimaryBilling, setIsDisabledPrimayBilling] =
    useState(isReadOnlyQuote);
  const [isDisabledEsign, setIsDisabledEsign] = useState(isReadOnlyQuote);
  const { isAccepted } = getQuoteStatus(quote);

  useEffect(() => {
    const quoteStatus = quote?.status;
    if (!quoteStatus) {
      return;
    }
    const disabled =
      isReadOnlyQuote || QUOTE_READONLY_STATUSES.has(quoteStatus);
    setIsDisabled(disabled);

    setIsDisabledPrimayBilling(
      isReadOnlyQuote ||
        !QUOTE_PRIMARY_BILLING_CONTACT_EDITABLE_STATUSES.has(quoteStatus),
    );
    setIsDisabledEsign(
      isReadOnlyQuote ||
        !QUOTE_ESIGN_CONTACT_EDITABLE_STATUSES.has(quoteStatus),
    );
  }, [isReadOnly, quote?.status, isAccepted, isReadOnlyQuote]);

  const [quoteContacts, setQuoteContacts] = useState<IQuoteContacts>(
    initialQuoteContacts || defaultQuoteContacts,
  );

  useEffect(() => {
    setBillingAccountId(quote?.billingAccountId || quote?.accountId || null);
  }, [quote?.billingAccountId, quote?.accountId]);

  // When quote' shipping contact changes, load the accountId accordingly
  useEffect(() => {
    if (shippingAccountId && !quoteContacts.primary?.accountId) {
      return;
    }

    if (quoteContacts.primary?.accountId) {
      setShippingAccountId(quoteContacts.primary?.accountId);
    } else {
      setShippingAccountId(quote?.accountId!);
    }
  }, [quoteContacts.primary?.accountId, quote?.accountId, shippingAccountId]);

  const addressDataForm = useForm<
    IQuoteContactAddressDataInputSchema,
    unknown,
    IQuoteContactAddressDataSchema
  >({
    mode: 'onChange',
    resolver: zodResolver(QuoteContactAddressDataSchema),
    defaultValues: {
      addressSource: quote?.addressSource,
      shippingAddressId: quote?.shippingAddress?.id,
      billingAddressId: quote?.billingAddress?.id,
    },
  });
  const addressSource = addressDataForm.watch('addressSource');

  const internalAccountContacts = internalAccountEsignContacts || [];

  const [
    additionalRecipientSelectedContacts,
    setAdditionalRecipientSelectedContacts,
  ] = useState<IContactRespSchema[]>(
    initialQuoteContacts?.additionalESignRecipients || [],
  );

  const [additionalSigners, setAdditionalSigners] = useState<
    IContactRespSchema[]
  >(initialQuoteContacts?.additionalSigners || []);

  useEffect(() => {
    if (addressSource !== quote?.addressSource || !quote) {
      addressDataForm.reset({
        addressSource: quote?.addressSource,
        shippingAddressId: quote?.shippingAddress?.id,
        billingAddressId: quote?.billingAddress?.id,
      });
    }
  }, [quote]);

  const handleChangeContact = async (
    contact: IContactRespSchema,
    type: ContactTypes,
    isInternal: boolean,
  ) => {
    const newQuoteContacts =
      type === 'esign'
        ? {
            ...quoteContacts,
            esign: [
              ...quoteContacts.esign.filter((c) => c.internal !== isInternal),
              contact,
            ],
          }
        : {
            ...quoteContacts,
            [type]: contact,
          };

    setQuoteContacts(newQuoteContacts);
    const quoteContactLatest = newQuoteContacts || quoteContacts;

    if (
      linkedAccount &&
      (!quoteContactLatest.billing?.id || !quoteContactLatest.primary?.id) &&
      type !== 'esign'
    ) {
      return;
    }

    handleSave({
      newQuoteContacts,
    });
  };

  const handleSave = async ({
    newQuoteContacts,
    newAdditionalSigners,
    newAdditionalRecipientSelectedContacts,
  }: {
    newQuoteContacts?: IQuoteContacts;
    newAdditionalSigners?: IContactRespSchema[];
    newAdditionalRecipientSelectedContacts?: IContactRespSchema[];
  }) => {
    try {
      setIsLoading(true);
      const quoteContactLatest = newQuoteContacts || quoteContacts;
      const additionalSignerLatest = newAdditionalSigners || additionalSigners;
      const additionalRecipientSelectedContactsLatest =
        newAdditionalRecipientSelectedContacts ||
        additionalRecipientSelectedContacts;

      const request: IQuoteContactsRequest = {
        primaryId:
          quoteContactLatest.primary?.id ||
          (!linkedAccount ? quoteContactLatest.billing?.id : null), // only when linkedAccount is not set, default to billing
        billingId:
          quoteContactLatest.billing?.id ||
          (!linkedAccount ? quoteContactLatest.primary?.id : null), // only when linkedAccount is not set, default to shipping
        esignCcIds: additionalRecipientSelectedContactsLatest.map(
          (recipientContacts) => recipientContacts.id, // additional contact ids for save
        ),
        esignIds: quoteContactLatest.esign.map(({ id }) => id),
        additionalSignerIds: additionalSignerLatest.map(({ id }) => id),
      };

      setQuoteContacts(
        await enqueueForQuote(quoteId!, () =>
          updateContactByQuote(quoteId!, request),
        ),
      );
      // Outside the queued body: it queues behind the contacts save.
      quote?.id && evaluateQuoteRules(quote.id);
    } catch (error) {
      handleApiErrorToast(error);
    }
    setIsLoading(false);
  };

  /**
   * Remove a additionalContact from the quote
   */
  const handleAdditionalContactRemove = (contactId: string) => {
    const newContacts = additionalRecipientSelectedContacts.filter(
      (currContact) => currContact.id !== contactId,
    );
    setAdditionalRecipientSelectedContacts(newContacts);

    handleSave({
      newAdditionalRecipientSelectedContacts: newContacts,
    });
  };
  /**
   * Called when a additional contact is selected for adding to the quote
   */
  const handleSelectedAdditionalRecipients = (contact: IContactRespSchema) => {
    setAdditionalRecipientSelectedContacts([
      ...additionalRecipientSelectedContacts,
      contact,
    ]);

    handleSave({
      newAdditionalRecipientSelectedContacts: [
        ...additionalRecipientSelectedContacts,
        contact,
      ],
    });
  };
  /**
   * Called when a brand new Additional Contact is Created and show as selected
   */
  const handleCreatedNewAdditionalContact = (contact: IContactRespSchema) => {
    setAdditionalRecipientSelectedContacts([
      ...additionalRecipientSelectedContacts,
      contact,
    ]);

    handleSave({
      newAdditionalRecipientSelectedContacts: [
        ...additionalRecipientSelectedContacts,
        contact,
      ],
    });
  };

  const handleAdditionalSignerRemove = (contactId: string) => {
    const newSigners = additionalSigners.filter(
      (currContact) => currContact.id !== contactId,
    );
    setAdditionalSigners(newSigners);

    handleSave({
      newAdditionalSigners: newSigners,
    });
  };
  const handleAdditionalSignerAdd = (contact: IContactRespSchema) => {
    const newSigners = [...additionalSigners, contact];
    setAdditionalSigners(newSigners);

    handleSave({
      newAdditionalSigners: newSigners,
    });
  };
  const handleAdditionalSignerSelect = (contact: IContactRespSchema) => {
    setAdditionalSigners([...additionalSigners, contact]);

    handleSave({
      newAdditionalSigners: [...additionalSigners, contact],
    });
  };

  const addressData = addressDataForm.getValues();
  const onSaveAddress = async () => {
    setIsLoading(true);
    try {
      await enqueueForQuote(quoteId!, () =>
        updateQuoteAddress(quoteId!, addressData),
      );
    } catch (err) {
      handleApiErrorToast(err);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    (async () => {
      if (addressDataForm.formState.isDirty) {
        await onSaveAddress();
        addressDataForm.reset(addressDataForm.getValues(), {
          keepValues: true,
        });
      }
    })();
  }, [addressDataForm.formState.isDirty]);

  return {
    quoteContacts,

    isLoading,
    internalAccountContacts,

    additionalRecipientSelectedContacts,
    handleSelectedAdditionalRecipients,
    handleCreatedNewAdditionalContact,
    handleAdditionalContactRemove,

    additionalSigners,
    handleAdditionalSignerRemove,
    handleAdditionalSignerAdd,
    handleAdditionalSignerSelect,

    addressSource,
    addressDataForm,
    onSaveAddress,
    handleChangeContact,

    isDisabled,
    isDisabledPrimaryBilling,
    isDisabledEsign,
    isEsignEnabled,
    loadingInternalEsignContacts,

    linkedAccount,
    handleChangeLinkedAccount,

    billingAccountId,
    handleChangeBillingAccount,

    accountDetails,

    shippingAccountId,
    handleChangeShippingAccountId,

    esignCustomerAccountId,
    handleEsignCustomerAccountIdChange,
    handleResetEsignContacts,

    accountSelectItems,
  };
}
